import { portalText } from "../i18n/portal.js";
/**
 * The SSE transport.
 *
 * The generated client's `send` returns a parsed body, which is exactly wrong for a channel
 * that never ends — so the five stream operations are reached with `EventSource` instead.
 * That does **not** mean the URL is hand-written: {@link streamUrlOf} resolves the path out
 * of `API_OPERATION`, the generated operation table, so a stream the spec does not document
 * cannot be opened and a path that moves in the spec moves here too.
 *
 * `withCredentials` is on because the console authenticates with the session cookie on
 * `app.wetalk.io` (the spec's second server). `EventSource` cannot send an `Authorization`
 * header at all, which is one more reason the console is the cookie-authenticated client.
 */
import { API_OPERATION } from "@wetalk/api-client";
import { SSE_EVENT_NAME } from "@wetalk/domain";

import type { StreamFrame, StreamListener, StreamSubscription } from "./gateway.js";

export class UnknownStreamOperationError extends Error {
  override readonly name = "UnknownStreamOperationError";
  constructor(operationId: string) {
    super(
      portalText("shell", "value_is_not_an_operation_of_the_assembled_openapi_documen_1c7a5983", { v0: String(operationId) }),
    );
  }
}

/**
 * Builds a stream URL from the spec's own path template.
 *
 * `/v1/stream/build/{build_job_id}` plus `{ build_job_id: "…" }` becomes an absolute URL.
 * A placeholder with no argument raises: a stream opened against a literal `{id}` would 404
 * at runtime and look like a server fault.
 */
export function streamUrlOf(
  baseUrl: string,
  operationId: string,
  pathParameter: Readonly<Record<string, string>>,
): string {
  const operation = API_OPERATION.find((one) => one.operationId === operationId);
  if (operation === undefined) throw new UnknownStreamOperationError(operationId);

  const path = operation.path.replaceAll(/\{([^}]+)\}/g, (_whole, name: string) => {
    const value = pathParameter[name];
    if (value === undefined || value === "") {
      throw new Error(`${operationId} needs a value for the path parameter "${name}".`);
    }
    return encodeURIComponent(value);
  });

  return `${baseUrl}${path}`;
}

/**
 * How long to wait before each reopen of a stream the browser closed on an HTTP error. The browser
 * reconnects by itself only after a network drop; an HTTP error (a 404 while the conversation's row
 * is not written yet — the browser test opens its transcript stream before the runtime writes the
 * row at first audio) closes the `EventSource` for good. About a minute in all, then it stays closed.
 */
export const STREAM_REOPEN_DELAY_MS: readonly number[] = Object.freeze([1_000, 2_000, 3_000, 5_000, 5_000, 5_000, 10_000, 10_000, 10_000, 10_000]);

/** The seams a suite drives; a browser gets the DOM's own. */
export interface EventStreamDependencies {
  readonly createSource: (url: string) => EventSource;
  readonly setTimer: (run: () => void, ms: number) => () => void;
  readonly reopenDelayMs: readonly number[];
}

const BROWSER_STREAM_DEPENDENCIES: EventStreamDependencies = {
  // glossary-lint-disable-next-line credential -- EventSource's own option name, fixed by the DOM API
  createSource: (url) => new EventSource(url, { withCredentials: true }),
  setTimer: (run, ms) => {
    const timer = setTimeout(run, ms);
    return () => {
      clearTimeout(timer);
    };
  },
  reopenDelayMs: STREAM_REOPEN_DELAY_MS,
};

/**
 * Opens one stream and subscribes to every event name the domain enumerates. A stream the browser
 * closed on an HTTP error is reopened after {@link STREAM_REOPEN_DELAY_MS}; a stream that opened
 * resets the count. `close()` stops it and any pending reopen.
 */
export function openEventStream(
  url: string,
  onEvent: StreamListener,
  dependencies: EventStreamDependencies = BROWSER_STREAM_DEPENDENCIES,
): StreamSubscription {
  let closed = false;
  let failures = 0;
  let source: EventSource | null = null;
  let cancelReopen: (() => void) | null = null;

  const connect = (): void => {
    if (closed) return;
    const current = dependencies.createSource(url);
    source = current;
    for (const name of SSE_EVENT_NAME) {
      current.addEventListener(name, (message: MessageEvent<string>) => {
        onEvent(frameOf(name, message));
      });
    }
    current.addEventListener("open", () => {
      failures = 0;
    });
    current.addEventListener("error", () => {
      // CONNECTING: the browser is already retrying a dropped connection. CLOSED: it gave up (an HTTP error).
      if (closed || current.readyState !== current.CLOSED) return;
      const delay = dependencies.reopenDelayMs[failures];
      if (delay === undefined) return;
      failures += 1;
      cancelReopen = dependencies.setTimer(() => {
        cancelReopen = null;
        connect();
      }, delay);
    });
  };
  connect();

  return {
    close(): void {
      closed = true;
      cancelReopen?.();
      source?.close();
    },
  };
}

function frameOf(name: string, message: MessageEvent<string>): StreamFrame {
  let data: unknown = message.data;
  try {
    data = JSON.parse(message.data);
  } catch {
    // A frame whose data is not JSON is still a frame; the caller decides what to do with
    // it. Swallowing the parse error here is not a fallback value — nothing is substituted.
  }
  return { id: message.lastEventId, event: name, data };
}

// ---------------------------------------------------------------------------------------
// Closing every scoped stream on a scope change — plan-014 A4 (FR12-AUTH-11, design §27.6.4)
// ---------------------------------------------------------------------------------------

/**
 * The streams a gateway has open, so a scope change can close them all.
 *
 * An `EventSource` cannot send `X-WeTalk-Scope-Generation`, and the server's SSE mount
 * authenticates outside the pipeline, so the server cannot refuse a stream the way it refuses a
 * stale request (`apps/api/src/http/scope-generation.ts`). The console closes them instead: every
 * stream the gateway opens is tracked here, and `closeAll` runs the moment this tab switches or
 * learns the scope moved. A page that still wants live updates opens a new stream in the new scope.
 */
export interface StreamRegistry {
  /** Tracks `subscription` until it is closed, by its owner or by {@link StreamRegistry.closeAll}. */
  track(subscription: StreamSubscription): StreamSubscription;
  /** Closes every tracked stream. Returns how many were open. */
  closeAll(): number;
}

export function streamRegistry(): StreamRegistry {
  const open = new Set<StreamSubscription>();
  return {
    track(subscription) {
      const tracked: StreamSubscription = {
        close(): void {
          open.delete(tracked);
          subscription.close();
        },
      };
      open.add(tracked);
      return tracked;
    },
    closeAll() {
      const closing = [...open];
      for (const one of closing) one.close();
      return closing.length;
    },
  };
}
